import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sprout, ArrowRight } from "lucide-react";

export default function Onboarding() {
  const { onboard } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    stable_name: "",
    country: "",
    admin_full_name: "",
    admin_email: "",
    admin_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.admin_password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    const res = await onboard(form);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error || "Onboarding failed");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-5" data-testid="onboarding-page">
      <div
        className="hidden md:flex md:col-span-2 flex-col justify-between p-10"
        style={{
          background: "#3B2F1E",
          color: "#F5EFE6",
          backgroundImage:
            "radial-gradient(circle at 80% 20%, rgba(212,169,106,0.18), transparent 40%), radial-gradient(circle at 20% 80%, rgba(124,92,58,0.4), transparent 40%)",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: "#7C5C3A" }}>
            <Sprout size={24} color="#F5EFE6" />
          </div>
          <div>
            <div className="font-heading" style={{ color: "#F5EFE6", fontSize: 32, letterSpacing: "-0.02em", lineHeight: 1 }}>StablOS</div>
            <div className="text-xs mt-1" style={{ color: "#D4A96A", letterSpacing: "0.08em", textTransform: "uppercase" }}>Equine ERP</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading" style={{ fontSize: 28, color: "#F5EFE6", lineHeight: 1.2 }}>
            Set up your stable in 60 seconds.
          </h1>
          <p className="mt-4 max-w-md" style={{ color: "#D4A96A" }}>
            We'll create your tenant workspace. All horse records, staff, and operations stay scoped to your stable.
          </p>
          <ul className="mt-6 space-y-2 text-sm" style={{ color: "#F5EFE6" }}>
            <li className="flex items-center gap-2"><span style={{ color: "#D4A96A" }}>•</span> Unlimited trial on Professional plan</li>
            <li className="flex items-center gap-2"><span style={{ color: "#D4A96A" }}>•</span> Multi-role staff access (admin, manager, vet, groom, driver, owner)</li>
            <li className="flex items-center gap-2"><span style={{ color: "#D4A96A" }}>•</span> Bank-grade tenant isolation</li>
          </ul>
        </div>
        <div className="text-xs" style={{ color: "rgba(245,239,230,0.5)" }}>
          © {new Date().getFullYear()} StablOS
        </div>
      </div>

      <div className="md:col-span-3 flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="se-card w-full max-w-xl" data-testid="onboarding-form">
          <h2 className="font-heading mb-1" style={{ fontSize: 22 }}>Create your stable account</h2>
          <p className="text-sm mb-6" style={{ color: "#8C9E8A" }}>
            One stable. One admin. You can add staff after.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="se-label block mb-2">Stable name</label>
              <input
                className="se-input"
                value={form.stable_name}
                onChange={update("stable_name")}
                required
                data-testid="onboarding-stable-name-input"
                placeholder="Sunrise Equestrian"
              />
            </div>
            <div>
              <label className="se-label block mb-2">Country</label>
              <input
                className="se-input"
                value={form.country}
                onChange={update("country")}
                required
                data-testid="onboarding-country-input"
                placeholder="Greece"
              />
            </div>
          </div>

          <label className="se-label block mb-2">Admin full name</label>
          <input
            className="se-input mb-4"
            value={form.admin_full_name}
            onChange={update("admin_full_name")}
            required
            data-testid="onboarding-admin-name-input"
            placeholder="Maria Papadopoulou"
          />

          <label className="se-label block mb-2">Admin email</label>
          <input
            className="se-input mb-4"
            type="email"
            value={form.admin_email}
            onChange={update("admin_email")}
            required
            data-testid="onboarding-email-input"
            placeholder="admin@yourstable.com"
            autoComplete="email"
          />

          <label className="se-label block mb-2">Password</label>
          <input
            className="se-input mb-6"
            type="password"
            value={form.admin_password}
            onChange={update("admin_password")}
            required
            minLength={8}
            data-testid="onboarding-password-input"
            placeholder="At least 8 characters"
            autoComplete="new-password"
          />

          {error && (
            <div
              className="mb-4 text-sm px-3 py-2 rounded-lg"
              style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
              data-testid="onboarding-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="se-btn w-full mb-4"
            disabled={loading}
            data-testid="onboarding-submit-button"
          >
            {loading ? "Creating workspace…" : (<>Create stable account <ArrowRight size={16} /></>)}
          </button>

          <div className="text-sm text-center" style={{ color: "#8C9E8A" }}>
            Already have an account?{" "}
            <Link to="/login" className="se-link" data-testid="onboarding-to-login-link">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
